import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import type { RaceData } from '../../api/types';
import RaceTimeline from './RaceTimeline';
import RaceTimelineBasic from './RaceTimelineBasic';

/**
 * The race graph with two views: the traditional one-line graph (the
 * default) and the one-row-per-racer graph (advanced). A grey underlined
 * word in the corner swaps them. Opening "advanced" plays the race in; going
 * back to "traditional" first plays it in reverse, then the traditional graph
 * slides back. The choice is remembered per browser.
 */

const STORAGE_KEY = 'aotd.raceView';

type View = 'traditional' | 'advanced';

const Wrap = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
`;

export const ViewToggle = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: 0.7rem;
  font-style: italic;
  color: #9ca3af;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;

  &:hover {
    color: #4b5563;
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

function readStored(): View {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'advanced' ? 'advanced' : 'traditional';
  } catch {
    return 'traditional';
  }
}

function store(view: View) {
  try {
    window.localStorage.setItem(STORAGE_KEY, view);
  } catch {
    // private window: the toggle still works for this visit
  }
}

interface RaceViewProps {
  race: RaceData;
  generationTimeMs?: number;
}

const RaceView: FC<RaceViewProps> = ({ race, generationTimeMs }) => {
  const [view, setView] = useState<View>(readStored);
  const [rewinding, setRewinding] = useState(false);
  const reduce = useReducedMotion();
  const advanced = view === 'advanced';

  const toggle = () => {
    if (rewinding) return;
    if (advanced) {
      // the graph plays itself backwards first, then hands over (onRewound)
      setRewinding(true);
      store('traditional');
      return;
    }
    setView('advanced');
    store('advanced');
  };

  const rewound = () => {
    setRewinding(false);
    setView('traditional');
  };

  return (
    <Wrap>
      <ViewToggle
        type="button"
        onClick={toggle}
        disabled={rewinding}
        aria-pressed={advanced}
        style={advanced ? { right: 84, top: 14 } : { top: 20 }}
      >
        {advanced ? 'traditional' : 'advanced'}
      </ViewToggle>
      <motion.div layout={!reduce} transition={{ duration: 0.3, ease: 'easeOut' }} style={{ overflow: 'hidden' }}>
        <AnimatePresence mode="popLayout" initial={false}>
          {advanced ? (
            // the advanced graph brings its own entrance (the race sweep) and
            // exit (the rewind), so the wrapper only fades
            <motion.div
              key="advanced"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <RaceTimeline race={race} generationTimeMs={generationTimeMs} rewind={rewinding} onRewound={rewound} />
            </motion.div>
          ) : (
            <motion.div
              key="traditional"
              initial={reduce ? { opacity: 0 } : { opacity: 0, x: -32 }}
              animate={{ opacity: 1, x: 0 }}
              exit={reduce ? { opacity: 0 } : { opacity: 0, x: -32 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              <RaceTimelineBasic race={race} generationTimeMs={generationTimeMs} />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </Wrap>
  );
};

export default RaceView;
